import type {Dictionary} from "@/shared/i18n/types";
import type {NamespacedTranslateFunction} from "@/shared/i18n/types";

/**
 * Normalize fee label for translation lookup (remove spaces, lowercase)
 */
function normalizeFeeLabel(label: string): string {
    return label.replace(/\s+/g, "").toLowerCase();
}

/**
 * Translate fee label if translation exists, otherwise return original label from API
 *
 * @param label - Original fee label from API (e.g., "Pet Fee", "Cleaning Fee")
 * @param t - Translation function from useTranslation("booking")
 * @param dict - Full dictionary from useTranslation("booking")
 * @returns Translated label if found, otherwise original label from API
 *
 * @example
 * const {t, dict} = useTranslation("booking");
 * const translated = translateFeeLabel("Pet Fee", t, dict);

 */
export function translateFeeLabel(label: string, t: NamespacedTranslateFunction<"booking">, dict: Dictionary): string {
    const normalized = normalizeFeeLabel(label);
    // Use relative key since t() is namespaced to "booking"
    const translationKey = `feeLabels.${normalized}`;
    const fullKeyPath = `booking.${translationKey}`;

    // Check if translation key exists in dictionary by traversing the path
    // Example: "booking.feeLabels.petfee" → ["booking", "feeLabels", "petfee"]
    const keys = fullKeyPath.split(".");
    let value: unknown = dict;

    for (const key of keys) {
        if (value && typeof value === "object" && key in value) {
            // Navigate deeper into the dictionary
            value = (value as Record<string, unknown>)[key];
        } else {
            // Key not found at any level, return original label from API
            return label;
        }
    }

    // Key exists in dictionary, use t() to get translation
    // This ensures we get proper interpolation/pluralization if needed
    if (typeof value === "string") {
        // Use type assertion since translationKey is dynamically constructed
        // and we know it's a valid key in the "booking" namespace
        const translated = t(translationKey as Parameters<typeof t>[0]);
        // If t() returns the key itself (translation not found), return original label
        // This handles edge cases where key exists but translation is missing
        if (translated === translationKey || translated === fullKeyPath) {
            return label;
        }
        return translated;
    }

    // Value exists but is not a string, return original label from API
    return label;
}

/**
 * Translate tax label if translation exists, otherwise return original label from API
 *
 * @param label - Original tax label from API (e.g., "State Occupancy Tax", "Sales Tax")
 * @param t - Translation function from useTranslation("booking")
 * @param dict - Full dictionary from useTranslation("booking")
 * @returns Translated label if found, otherwise original label from API
 *
 * @example
 * const {t, dict} = useTranslation("booking");
 * const translated = translateTaxLabel("State Occupancy Tax", t, dict);
 */
export function translateTaxLabel(label: string, t: NamespacedTranslateFunction<"booking">, dict: Dictionary): string {
    const normalized = normalizeFeeLabel(label);
    // Use relative key since t() is namespaced to "booking"
    const translationKey = `taxLabels.${normalized}`;
    const fullKeyPath = `booking.${translationKey}`;

    // Check if translation key exists in dictionary by traversing the path
    // Example: "booking.taxLabels.stateoccupancytax" → ["booking", "taxLabels", "stateoccupancytax"]
    const keys = fullKeyPath.split(".");
    let value: unknown = dict;

    for (const key of keys) {
        if (value && typeof value === "object" && key in value) {
            // Navigate deeper into the dictionary
            value = (value as Record<string, unknown>)[key];
        } else {
            // Key not found at any level, return original label from API
            return label;
        }
    }

    // Key exists in dictionary, use t() to get translation
    // This ensures we get proper interpolation/pluralization if needed
    if (typeof value === "string") {
        // Use type assertion since translationKey is dynamically constructed
        // and we know it's a valid key in the "booking" namespace
        const translated = t(translationKey as Parameters<typeof t>[0]);
        // If t() returns the key itself (translation not found), return original label
        // This handles edge cases where key exists but translation is missing
        if (translated === translationKey || translated === fullKeyPath) {
            return label;
        }
        return translated;
    }

    // Value exists but is not a string, return original label from API
    return label;
}
